import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Divider,
  Avatar
} from '@mui/material';
import { Visibility, VisibilityOff, FitnessCenter, LockOutlined } from '@mui/icons-material';
import { useFormValidation } from '../hooks/useFormValidation';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));


  const [showPassword, setShowPassword] = useState(false);

  const initialData = { email: '', password: '' };

  const validationRules = {
    email: [
      (value) => !value ? t('forms:validation.email.required') : null,
      (value) => !/\S+@\S+\.\S+/.test(value) ? t('forms:validation.email.invalid') : null
    ],
    password: [
      (value) => !value ? t('forms:validation.password.required') : null
    ]
  };

  const {
    data: formData,
    isSubmitting,
    updateField,
    handleSubmit,
    getFieldError,
    hasFieldError
  } = useFormValidation(initialData, validationRules);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);
  

  const onFormSubmit = async (data) => {
    clearError();

    const result = await login(data);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100vh', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: theme => theme.palette.background.default
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img src="/logo512.png" alt="MetuMotion Logo" width="120" height="120" />
          </motion.div>
          <Typography variant="h6" sx={{ mt: 2 }}>{t('common:loading.default')}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      bgcolor: theme => theme.palette.background.default,
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Background gradient effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[800]} 35%, ${theme.palette.primary[700]} 100%)`,
          opacity: 0.95,
          zIndex: 0
        }}
      />

      {/* Decorative circles */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: theme.palette.accent.mint,
            filter: 'blur(80px)',
            opacity: 0.2,
            top: '-50px',
            right: '-50px',
            zIndex: 1
          }}
        />
      </Fade>

      <Fade in timeout={1500}>
        <Box
          sx={{
            position: 'absolute',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: theme.palette.accent.yellow,
            filter: 'blur(70px)',
            opacity: 0.15,
            bottom: '-30px',
            left: '-30px',
            zIndex: 1
          }}
        />
      </Fade>

      {/* Language Switcher */}
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 3 }}>
        <LanguageSwitcher variant="full" />
      </Box>

      <Container maxWidth="lg" sx={{ zIndex: 2, my: 4, display: 'flex', alignItems: 'center' }}>
        <Grid container sx={{ width: '100%', height: '100%' }}>
          {/* Left side - Branding */}
          {!isMobile && (
            <Grid 
              size={{ md: 6 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
                color: 'white'
              }}
            >
              <Grow in timeout={1000}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                  >
                    <img 
                      src="/logo512.png" 
                      alt="MetuMotion Logo" 
                      style={{ 
                        width: '180px', 
                        height: '180px',
                        filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2))'
                      }} 
                    />
                  </motion.div>
                  
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <Typography 
                      variant="h2" 
                      component="h1" 
                      sx={{ 
                        fontWeight: 700, 
                        mt: 3, 
                        mb: 1,
                        color: 'white',
                        textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                      }}
                    >
                      {t('auth:brandText.title')}
                    </Typography>
                  </motion.div>
                  
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        opacity: 0.9,
                        maxWidth: '400px',
                        mx: 'auto',
                        mt: 2,
                        color: 'white'
                      }}
                    >
                      {t('auth:brandText.tagline')}
                    </Typography>
                  </motion.div>
                  
                  <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.8 }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          px: 3
                        }}
                      >
                        <FitnessCenter sx={{ fontSize: 40, color: theme.palette.accent.yellow, mb: 1 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9, textAlign: 'center' , color: theme.palette.accent.yellow}}>
                          {t('auth:login.features.trackProgress')}
                        </Typography>
                      </Box>
                    </motion.div>
                    
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                    
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 1 }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          px: 3
                        }}
                      >
                        <LockOutlined sx={{ fontSize: 40, color: theme.palette.accent.yellow, mb: 1 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9, textAlign: 'center' ,color: theme.palette.accent.yellow }}>
                          {t('auth:login.features.secureAccount')}
                        </Typography>
                      </Box>
                    </motion.div>
                  </Box>
                </Box>
              </Grow>
            </Grid>
          )}
          
          {/* Right side - Login Form */}
          <Grid 
            size={{ xs: 12, md: 6 }}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              p: { xs: 2, sm: 4 }
            }}
          >
            <Fade in timeout={800}>
              <Paper 
                elevation={24} 
                sx={{ 
                  width: '100%', 
                  maxWidth: 450,
                  borderRadius: 3,
                  p: { xs: 3, sm: 5 },
                  backdropFilter: 'blur(10px)',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                {/* Mobile logo */}
                {isMobile && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <Avatar 
                      src="/logo192.png" 
                      alt="MetuMotion Logo"
                      sx={{ 
                        width: 80, 
                        height: 80,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}
                    />
                  </Box>
                )}
                
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 'bold', 
                      mb: 1,
                      color: theme.palette.primary.main
                    }}
                  >
                    {t('auth:login.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('auth:login.subtitle')}
                  </Typography>
                </Box>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': { color: theme.palette.error.main }
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        required
                        fullWidth
                        id="email"
                        label={t('auth:login.form.email.label')}
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        error={hasFieldError('email')}
                        helperText={getFieldError('email')}
                        variant="outlined"
                        InputProps={{
                          sx: { borderRadius: 2 }
                        }}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        required
                        fullWidth
                        name="password"
                        label={t('auth:login.form.password.label')}
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={formData.password}
                        onChange={(e) => updateField('password', e.target.value)}
                        error={hasFieldError('password')}
                        helperText={getFieldError('password')}
                        variant="outlined"
                        InputProps={{
                          sx: { borderRadius: 2 },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleTogglePasswordVisibility}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Link
                      component={RouterLink}
                      to="/forgot-password"
                      underline="hover"
                      variant="body2"
                      sx={{
                        fontWeight: 'medium',
                        color: theme.palette.primary.main,
                        transition: 'color 0.2s',
                        '&:hover': {
                          color: theme.palette.accent.mint
                        }
                      }}
                    >
                      {t('auth:login.links.forgotPassword')}
                    </Link>
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{ 
                        py: 1.5, 
                        borderRadius: 2,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent.mint} 100%)`,
                        boxShadow: '0 4px 10px rgba(17, 138, 178, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 6px 15px rgba(17, 138, 178, 0.4)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      {isSubmitting ? t('auth:login.buttons.signingIn') : t('auth:login.buttons.signIn')}
                    </Button>
                  </Box>

                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('auth:login.links.noAccount')}{' '}
                      <Link 
                        component={RouterLink} 
                        to="/email-verification" 
                        underline="hover" 
                        sx={{ 
                          fontWeight: 'medium',
                          color: theme.palette.primary.main,
                          transition: 'color 0.2s',
                          '&:hover': {
                            color: theme.palette.accent.mint
                          }
                        }}
                      >
                        {t('auth:login.links.signUp')}
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginPage;